import { useEffect } from 'react';
import { Box, Paper } from '@material-ui/core';
import { CloudDownload } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Layout } from '@roc/feature-app-core';
import { GridColumnProps, IconButton } from '@roc/ui';
import { PDFPreview } from '@roc/client-portal-shared/components';
import { observer } from 'mobx-react';
import { filterProps, noOpComparator } from '@roc/ui/utils';
import { useLenderLicensesStore } from '../hooks/useLenderLicensesStore';
import { LenderLicensesGrid } from './lenderLicensesGrid';
import { LICENSE_TYPE_OPTIONS, US_STATE_OPTIONS } from '../constants/lenderLicenseConstants';

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '100%',
    paddingLeft: '16px',
    paddingRight: '16px',
    margin: '0 auto',
  },
  paper: {
    boxShadow: '0 0.5px 2px rgba(0, 0, 0, 0.08) !important',
    border: '0.5px solid #e0e0e0',
    padding: '16px',
    borderRadius: '12px !important',
    backgroundColor: '#fff',
  },
}));

export const LenderLicensesDashboard = observer(() => {
  const classes = useStyles();
  const { lenderLicensesStore: store } = useLenderLicensesStore();

  useEffect(() => {
    const timer = setTimeout(() => {
      store.resetAndFetchGridData();
    }, 500);
    return () => {
      clearTimeout(timer);
      store.reset();
    };
  }, []);

  const columns: GridColumnProps[] = [
    {
      field: 'lenderAlias',
      headerName: 'Lender Alias',
      width: 180,
      ...filterProps(),
      comparator: noOpComparator,
    },
    {
      field: 'lenderFullName',
      headerName: 'Lender Full Name',
      width: 220,
      ...filterProps(),
      comparator: noOpComparator,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      comparator: noOpComparator,
      filter: 'selectFilter',
      floatingFilter: true,
      floatingFilterComponent: 'selectFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
        options: LICENSE_TYPE_OPTIONS,
        testId: 'type-filter',
      },
    },
    {
      field: 'originatorName',
      headerName: 'Originator',
      width: 180,
      ...filterProps(),
      comparator: noOpComparator,
    },
    {
      field: 'state',
      headerName: 'State',
      width: 90,
      comparator: noOpComparator,
      filter: 'selectFilter',
      floatingFilter: true,
      floatingFilterComponent: 'selectFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
        options: US_STATE_OPTIONS,
        testId: 'state-filter',
      },
    },
    {
      field: 'expirationDate',
      headerName: 'Expiration Date',
      width: 140,
      comparator: noOpComparator,
      valueFormatter: ({ value }) => {
        if (!value) return '';
        const d = new Date(value);
        return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
      },
    },
    {
      field: 'lenderDocumentId',
      headerName: 'Document',
      width: 100,
      comparator: noOpComparator,
      cellRendererFramework: params => {
        if (!params.node.data) return null;
        const { lenderDocumentId, type, state } = params.node.data;
        if (!lenderDocumentId) return null;
        const title = [type, state].filter(Boolean).join(' - ');
        return (
          <IconButton
            testId={`preview-license-doc-${lenderDocumentId}`}
            onClick={() => store.previewLicenseDocument(lenderDocumentId, title)}
          >
            <CloudDownload color="primary" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <Layout title="Lenders Licenses" containerClass={classes.container}>
        <Box p={2}>
          <Paper className={classes.paper}>
            <LenderLicensesGrid
              gridStore={store.lenderLicensesGridStore}
              columns={columns}
            />
          </Paper>
        </Box>
      </Layout>

      <PDFPreview
        open={store.documentPreview != null}
        title={store.documentPreview?.title}
        data={store.documentPreview?.data}
        headers={store.documentPreview?.headers}
        onClose={() => store.closeDocumentPreview()}
      />
    </>
  );
});
